import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Bot, User, Send, AlertCircle, X, Mail } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SupportChat = ({ user, token, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState("");
  const [escalated, setEscalated] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Message de bienvenue
    setMessages([{
      role: "assistant",
      content: "👋 Bonjour ! Je suis MathBot Support, votre assistant technique.\n\nJe peux vous aider avec :\n• Problèmes de connexion\n• Questions sur votre compte\n• Abonnements et paiements\n• Problèmes techniques\n\nComment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }]);
  }, []);

  useEffect(() => {
    // Auto-scroll vers le bas
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const message = inputText.trim();
    if (!message || isLoading) return;

    // Ajouter le message utilisateur
    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText("");
    setIsLoading(true);
    setError("");

    // Préparer le message assistant vide pour streaming
    setMessages(prev => [...prev, {
      role: "assistant",
      content: "",
      timestamp: new Date()
    }]);

    try {
      const response = await fetch(`${API}/support/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: message,
          user_email: user.email
        })
      });

      if (!response.body) {
        throw new Error("Pas de réponse du serveur");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data:")) continue;

          try {
            const event = JSON.parse(line.slice(5));

            if (event.type === "delta") {
              // Mettre à jour le dernier message avec le nouveau contenu
              setMessages(prev => {
                const updated = [...prev];
                const lastMessage = updated[updated.length - 1];
                lastMessage.content += event.content;
                return updated;
              });
            } else if (event.type === "done") {
              if (event.conversation_id) {
                setConversationId(event.conversation_id);
              }
            } else if (event.type === "error") {
              setError(event.message);
            }
          } catch (e) {
            console.error("Erreur parsing SSE:", e);
          }
        }
      }
    } catch (err) {
      console.error("Erreur chat support:", err);
      setError("Erreur de connexion. Veuillez réessayer.");
      // Retirer le message vide en cas d'erreur
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const escalateToHuman = async () => {
    if (escalated) return;

    try {
      const summary = messages.map(m => `${m.role}: ${m.content}`).join("\n\n");

      const response = await axios.post(
        `${API}/support/escalate`,
        {
          conversation_id: conversationId || "new",
          user_email: user.email,
          summary: summary
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setEscalated(true);
      setMessages(prev => [...prev, {
        role: "system",
        content: "✅ Votre demande a été transmise à notre équipe support. Vous recevrez une réponse par email sous 24h à " + user.email,
        timestamp: new Date()
      }]);
    } catch (err) {
      console.error("Erreur escalade:", err);
      setError("Erreur lors de l'escalade. Contactez directement assistance@math-skill.com");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl h-[600px] bg-gray-900 border-green-700 flex flex-col">
        <CardHeader className="border-b border-green-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <div>
                <CardTitle className="text-green-400">Support Technique</CardTitle>
                <p className="text-green-300/70 text-sm">MathBot Support - Assistance en direct</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-green-300 hover:text-green-100"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`flex items-start space-x-2 max-w-[80%] ${
                      msg.role === "user" ? "flex-row-reverse space-x-reverse" : ""
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === "user"
                          ? "bg-blue-600"
                          : msg.role === "system"
                          ? "bg-green-600"
                          : "bg-green-700"
                      }`}
                    >
                      {msg.role === "user" ? (
                        <User className="w-4 h-4 text-white" />
                      ) : msg.role === "system" ? (
                        <AlertCircle className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div
                      className={`rounded-lg p-3 ${
                        msg.role === "user"
                          ? "bg-blue-600 text-white"
                          : msg.role === "system"
                          ? "bg-green-600/20 text-green-300 border border-green-600/30"
                          : "bg-gray-800 text-green-100"
                      }`}
                    >
                      <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                      <div className="text-xs opacity-50 mt-1">
                        {msg.timestamp.toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex items-center space-x-2 bg-gray-800 rounded-lg p-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Erreur */}
          {error && (
            <div className="px-4 py-2 bg-red-900/20 border-t border-red-800/30">
              <div className="flex items-center space-x-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-green-700/30 p-4 space-y-3">
            <div className="flex space-x-2">
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Décrivez votre problème..."
                disabled={isLoading}
                className="flex-1 bg-gray-800 border-green-700/50 text-green-100 placeholder-green-500/50"
              />
              <Button
                onClick={sendMessage}
                disabled={isLoading || !inputText.trim()}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={escalateToHuman}
              disabled={escalated || messages.length < 3}
              className="w-full border-green-700 text-green-300 hover:bg-green-950"
            >
              <Mail className="w-4 h-4 mr-2" />
              {escalated
                ? "✅ Demande transmise"
                : "Contacter un humain (assistance@math-skill.com)"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
